import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core'
import Card from 'common/Card'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { filterMountpoints } from 'store/slices/map-slice'
import {
  Client,
  useAutoStatsBases,
  useClientsList,
  useMountpointGroupsList,
  useMountpointsList,
  useNewMountpoint,
  useUsersList,
} from '../api'
import { formatDurationSince } from '../format-helpers'
import { GroupForm } from '../GroupForm'
import { CircleIcon } from '../icons'
import { strings } from '../strings'
import { SearchPanel } from './SearchPanel'
import RoomIcon from '@material-ui/icons/Room'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { useMapContext } from 'Map/context/MapProvider'
import { useToken } from 'App'

const BASES_LOOKBACK_MS = 24 * 60 * 60 * 1000

export function Mountpoints() {
  const classes = useStyles()
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<number | null>(null)
  const [creation, setCreation] = useState(false)
  const { mountpointGroupsList } = useMountpointGroupsList()
  const newMountpoint = useNewMountpoint()
  const { ut: userRole } = useToken()
  const isAdmin = userRole === 'admin'
  const [connectionsSort, setConnectionsSort] = useState<
    'desc' | 'asc' | null
  >(null)

  return (
    <>
      <Card title={strings.mountpoints}>
        <SearchPanel
          onChanged={(query, group) => {
            setQuery(query)
            setGroup(group)
          }}
          groups={mountpointGroupsList}
          showNew={isAdmin && creation}
          onNew={() => {
            if (!isAdmin) return
            newMountpoint(query, group)
              // .then((data) => console.log(data))
              .catch((err) => console.log(err.message))
          }}
        />
        {isAdmin && <GroupForm groupId={group} groupType="mountpoint" />}
      </Card>
      <Paper className={classes.result}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ width: 33, borderBottom: 'none' }}>&nbsp;</th>
              <th>{strings.name}</th>
              <th className={classes.durationCell}>{strings.connectedTime}</th>
              <th className={classes.createdByCell}>{strings.createdBy}</th>
              <th className={classes.connectionsCell}>
                <span
                  className={classes.sortableHeader}
                  onClick={() =>
                    setConnectionsSort((prev) =>
                      prev === null ? 'desc' : prev === 'desc' ? 'asc' : null,
                    )
                  }
                >
                  {strings.connections}
                  {connectionsSort === 'desc' && (
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      className={classes.sortIcon}
                    />
                  )}
                  {connectionsSort === 'asc' && (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      className={classes.sortIcon}
                    />
                  )}
                </span>
              </th>
              <th className={classes.actionCell} />
            </tr>
          </thead>
          <tbody>
            <DataTable
              query={query}
              group={group}
              onEmptyResult={setCreation}
              connectionsSort={connectionsSort}
            />
          </tbody>
        </table>
      </Paper>
    </>
  )
}

function DataTable({ query, group, onEmptyResult, connectionsSort }: any) {
  const classes = useStyles()

  const { flyToMountpointPosition } = useMapContext()

  const dispatch = useDispatch()
  const history = useHistory()
  const { mountpointsList, loading, error } = useMountpointsList(query, group)
  const { clients } = useClientsList()
  const { usersList } = useUsersList('', null)

  const [basesRange] = useState(() => ({
    from: new Date(Date.now() - BASES_LOOKBACK_MS).toISOString(),
    to: new Date().toISOString(),
  }))
  const { bases, loading: basesLoading } = useAutoStatsBases(
    basesRange.from,
    basesRange.to,
  )
  const [expandedMountpointId, setExpandedMountpointId] = useState<
    number | null
  >(null)

  const ownerByMountpoint = useMemo(() => {
    const map = new Map<string, string>()
    if (!bases) return map
    for (const b of bases) {
      if (b.mountpoint && b.ownerDisplayName) {
        map.set(b.mountpoint, b.ownerDisplayName)
      }
    }
    return map
  }, [bases])

  const clientsByMountpointId = useMemo(() => {
    const map = new Map<number, Client[]>()
    if (!clients) return map
    for (const c of clients) {
      if (c.mountpointId == null) continue
      const arr = map.get(c.mountpointId)
      if (arr) arr.push(c)
      else map.set(c.mountpointId, [c])
    }
    return map
  }, [clients])

  const userById = useMemo(() => {
    const map = new Map<
      number,
      { display_name: string; phone_number: string; username: string }
    >()
    if (!usersList) return map
    for (const u of usersList) {
      if (u.old_id != null) {
        map.set(u.old_id, {
          display_name: u.display_name,
          phone_number: u.phone_number,
          username: u.username,
        })
      }
    }
    return map
  }, [usersList])

  useEffect(() => {
    if (!query.length) onEmptyResult(false)
    else
      onEmptyResult(
        mountpointsList &&
          (mountpointsList.length === 0 || mountpointsList.length > 1),
      )
  }, [onEmptyResult, mountpointsList, query])

  useEffect(() => {
    if (!mountpointsList) return
    dispatch(filterMountpoints(mountpointsList.map((x) => x.id)))
    return () => {
      dispatch(filterMountpoints([]))
    }
  }, [mountpointsList])

  if (loading || basesLoading)
    return (
      <Row>
        <CircularProgress size={20} />
      </Row>
    )

  if (error)
    return (
      <Row>
        <Typography color="error">{error.message}</Typography>
      </Row>
    )

  if (!mountpointsList || !mountpointsList.length)
    return (
      <Row>
        <Typography color="error">{strings.notExists}</Typography>
      </Row>
    )

  return (
    <>
      {mountpointsList
        .sort((a, b) => {
          if (connectionsSort) {
            const ca = clientsByMountpointId.get(a.id)?.length || 0
            const cb = clientsByMountpointId.get(b.id)?.length || 0
            if (ca !== cb)
              return connectionsSort === 'desc' ? cb - ca : ca - cb
          }
          return b.connectedSince - a.connectedSince
        })
        .flatMap((x) => {
          const ownerName = ownerByMountpoint.get(x.name) || ''
          const mountpointClients = clientsByMountpointId.get(x.id) || []
          const count = mountpointClients.length
          const isExpanded = expandedMountpointId === x.id && count > 0
          const rows = [
            <tr key={x.id}>
              <td>
                <CircleIcon
                  style={{ color: x.connectedSince ? 'green' : 'lightgray' }}
                />
              </td>
              <td className={classes.nameCell}>
                <Tooltip title={x.name} arrow>
                  <Button
                    onClick={() => history.push(`/mountpoint/${x.id}/info`)}
                    classes={{ label: classes.nameLabel }}
                    style={{ textTransform: 'none' }}
                  >
                    {x.name}
                  </Button>
                </Tooltip>
              </td>
              <td className={classes.durationCell}>
                {formatDurationSince(x.connectedSince)}
              </td>
              <td className={classes.createdByCell}>
                <Tooltip title={ownerName} arrow>
                  <span className={classes.truncate}>{ownerName}</span>
                </Tooltip>
              </td>
              <td className={classes.connectionsCell}>
                {count > 0 ? (
                  <Button
                    size="small"
                    onClick={() =>
                      setExpandedMountpointId(isExpanded ? null : x.id)
                    }
                    className={classes.connectionsButton}
                  >
                    <span className={classes.connectionsCount}>{count}</span>
                    {isExpanded ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </Button>
                ) : null}
              </td>
              <td className={classes.actionCell}>
                {/* Mobile */}
                <IconButton
                  size="small"
                  onClick={() => {
                    history.push('/')
                    setTimeout(() => {
                      flyToMountpointPosition(x.id)
                    }, 250)
                  }}
                  className={classes.mobileLocation}
                >
                  <RoomIcon fontSize="small" />
                </IconButton>
                {/* Desktop */}
                <IconButton
                  size="small"
                  onClick={() => flyToMountpointPosition(x.id)}
                  className={classes.desktopLocation}
                >
                  <RoomIcon fontSize="small" />
                </IconButton>
              </td>
            </tr>,
          ]
          if (isExpanded) {
            rows.push(
              <tr
                key={`${x.id}-expanded`}
                className={classes.expandedRow}
              >
                <td />
                <td colSpan={5} className={classes.expandedCell}>
                  {mountpointClients.map((c, i) => {
                    const u = userById.get(c.userId)
                    const name =
                      u?.display_name || u?.username || `#${c.userId}`
                    const phone = u?.phone_number || ''
                    return (
                      <div key={i} className={classes.connectionLine}>
                        <span className={classes.userText}>{name}</span>
                        {phone && (
                          <>
                            <span className={classes.connectionArrow}>→</span>
                            <span
                              className={classes.phoneText}
                              style={{ direction: 'ltr', textAlign: 'left' }}
                            >
                              {phone}
                            </span>
                          </>
                        )}
                      </div>
                    )
                  })}
                </td>
              </tr>,
            )
          }
          return rows
        })}
    </>
  )
}

function Row({ children }: any) {
  return (
    <tr>
      <td colSpan={6} style={{ textAlign: 'center' }}>
        {children}
      </td>
    </tr>
  )
}

const useStyles = makeStyles((theme) => ({
  search: {
    flex: '0 1 auto',
    width: '100%',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: '#fff',
    '& .MuiButtonBase-root': {
      padding: theme.spacing(0.75),
    },
    '& .tags': {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      '& .MuiButtonBase-root': {
        borderRadius: theme.spacing(2),
        marginRight: theme.spacing(1),
        fontSize: '0.6rem',
        fontWeight: 'normal',
        padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
      },
    },
  },
  result: {
    flex: '1 1 auto',
    width: '100%',
    marginTop: theme.spacing(1),
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    overflowX: 'auto',
    overflowY: 'auto',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  table: {
    width: '100%',
    minWidth: 'max-content',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
    '& tr > td': {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(0.5),
      cursor: 'pointer',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    },
    '& th': {
      borderBottom: '1px solid lightgray',
      fontWeight: 'normal',
      textAlign: 'left',
      paddingBottom: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      whiteSpace: 'nowrap',
    },
    '& tr th:last-child, & tr td:last-child': {
      textAlign: 'right',
      paddingRight: 0,
    },
  },
  truncate: {
    display: 'inline-block',
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  nameCell: {
    whiteSpace: 'nowrap',
  },
  nameLabel: {
    display: 'block',
    whiteSpace: 'nowrap',
  },
  durationCell: {
    whiteSpace: 'nowrap',
  },
  createdByCell: {
    maxWidth: 140,
    whiteSpace: 'nowrap',
  },
  connectionsCell: {
    whiteSpace: 'nowrap',
    minWidth: 60,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  sortIcon: {
    fontSize: '1rem',
  },
  connectionsButton: {
    minWidth: 0,
    padding: `${theme.spacing(0.25)}px ${theme.spacing(0.75)}px`,
    textTransform: 'none',
  },
  connectionsCount: {
    display: 'inline-block',
    minWidth: 20,
    textAlign: 'center',
    fontWeight: 500,
    marginRight: theme.spacing(0.25),
  },
  expandedRow: {
    backgroundColor: '#fafafa',
  },
  expandedCell: {
    padding: theme.spacing(1),
    '&&': {
      cursor: 'default',
      whiteSpace: 'normal',
    },
  },
  connectionLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: `${theme.spacing(0.25)}px 0`,
    fontSize: '0.85rem',
  },
  userText: {
    display: 'inline-block',
    color: '#555',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    fontWeight: 500,
  },
  connectionArrow: {
    color: '#999',
  },
  phoneText: {
    display: 'inline-block',
    color: '#555',
    whiteSpace: 'nowrap',
    unicodeBidi: 'bidi-override',
    verticalAlign: 'middle',
  },
  actionCell: {
    width: 40,
    minWidth: 40,
    whiteSpace: 'nowrap',
  },
  mobileLocation: {
    padding: 1,
    '& svg': {
      marginTop: 0,
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  desktopLocation: {
    padding: 1,
    '& svg': {
      marginTop: 0,
    },
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'unset',
    },
  },
}))
